import { Injectable, inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { Apollo, gql } from 'apollo-angular';
import { pick } from 'lodash';
import { Observable, concatMap, map, of, tap } from 'rxjs';
import {
  ClinicalDataMutations,
  ClinicalDataMutationsCreateOrUpdateArgs,
  ClinicalDataResultForm
} from '../../../../../interfaces/schema';
import { AuthActions } from '../../../../components/auth/auth.actions';
import { Session } from '../../../../providers/session/session.service';
import { OcrActions } from './ocr.actions';
import { selectOcrData, selectOcrUploading } from './ocr.selectors';

/**
 * @description OCR Effects.
 * @exports
 * @class OcrEffects
 */
@Injectable()
export class OcrEffects {
  private readonly actions = inject(Actions);
  private readonly apollo = inject(Apollo);
  private readonly session = inject(Session);
  private readonly store = inject(Store);

  readonly onAuthLogin = createEffect(
    () => {
      return this.actions.pipe(
        ofType(AuthActions.login),
        concatMap(() => {
          const data = this.session.retrieve(`ocr-data`);
          const synchronized = this.session.retrieve(`ocr-data-synchronized`);

          if (Array.isArray(data) && data.length > 0) {
            if (typeof synchronized === 'boolean') {
              if (synchronized) return of(data);

              const form: Array<ClinicalDataResultForm> = data.map((o) =>
                pick(o, [
                  'id',
                  'code',
                  'name',
                  'referenceValue',
                  'unit',
                  'value'
                ])
              );

              return this.createClinicalData({
                form: { clinicalDataResult: form }
              });
            }
          }

          return of(null);
        }),
        tap((res) => {
          if (res !== null) {
            this.session.clear(`ocr-data`);
            this.session.clear(`ocr-data-synchronized`);
          }
        })
      );
    },
    { dispatch: false }
  );

  readonly onAuthLogout_SwitchProfile = createEffect(
    () => {
      return this.actions.pipe(
        ofType(AuthActions.logout, AuthActions.switchProfile),
        tap(() => {
          this.session.clear(`ocr-data`);
          this.session.clear(`ocr-progress`);
          this.session.clear(`ocr-uploading`);
          this.session.clear(`ocr-data-synchronized`);
        })
      );
    },
    { dispatch: false }
  );

  readonly onAuthSwitchProfile = createEffect(
    () => {
      return this.actions.pipe(
        ofType(AuthActions.switchProfile),
        map(() => OcrActions.reset())
      );
    },
    { dispatch: true }
  );

  readonly onSetData = createEffect(
    () => {
      return this.actions.pipe(
        ofType(OcrActions.setData),
        concatMap(() => this.store.select(selectOcrData)),
        tap((data) => {
          this.session.store(`ocr-data`, data);
        }),
        tap(() => {
          this.session.store(
            `ocr-data-synchronized`,
            this.session.isAuthenticated()
          );
        })
      );
    },
    { dispatch: false }
  );

  readonly onSetUploading = createEffect(
    () => {
      return this.actions.pipe(
        ofType(OcrActions.setUploading),
        concatMap(() => this.store.select(selectOcrUploading)),
        tap((uploading) => {
          this.session.store(`ocr-uploading`, uploading);
        })
      );
    },
    { dispatch: false }
  );

  /**
   * @description Create clinical data.
   * @param {ClinicalDataMutationsCreateOrUpdateArgs} variables Params.
   * @returns {Observable<ClinicalDataMutations['createOrUpdate']>}
   * @memberof OcrEffects
   */
  createClinicalData(
    variables?: ClinicalDataMutationsCreateOrUpdateArgs
  ): Observable<ClinicalDataMutations['createOrUpdate']> {
    const mutation = gql`
      mutation CreateClinicalData($form: ClinicalDataForm) {
        User {
          ClinicalData {
            createOrUpdate(form: $form) {
              id
            }
          }
        }
      }
    `;
    return this.apollo
      .mutate({ mutation, variables, fetchPolicy: 'no-cache' })
      .pipe(
        map(
          ({ data }) =>
            (data as any).User.ClinicalData
              .createOrUpdate as ClinicalDataMutations['createOrUpdate']
        )
      );
  }
}
